/**
 * Formats an RFC 3339 / ISO timestamp as a short locale string in the user's
 * timezone. Returns the original string unchanged if it doesn't parse.
 *
 * Returns "" during SSR — on the server `toLocaleString` would use the Node runtime's TZ (UTC in
 * prod), and Solid hydration may keep that wrong text. Client-only render guarantees the user's
 * local TZ.
 */
export function formatIsoTimestamp(iso: string): string {
  if (typeof window === "undefined") return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

const timeOfDayFormat = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });

/** Time of day only ("1:06 PM"), for a timestamp shown inside the turn it belongs to. */
export function formatIsoTime(iso: string): string {
  if (typeof window === "undefined") return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return timeOfDayFormat.format(date);
}

/**
 * Formats an RFC 3339 / ISO timestamp as a localized relative phrase
 * ("2 hours ago", "yesterday"). Returns the original string unchanged if it
 * doesn't parse. Returns "" during SSR so hydration doesn't keep a phrase
 * computed against the server's clock.
 */
export function formatRelativeTime(iso: string, locale?: string): string {
  if (typeof window === "undefined") return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const divisions: ReadonlyArray<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [7, "day"],
    [4.34524, "week"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"],
  ];
  let duration = (date.getTime() - Date.now()) / 1000;
  for (const [amount, unit] of divisions) {
    if (Math.abs(duration) < amount) return rtf.format(Math.round(duration), unit);
    duration /= amount;
  }
  return "";
}

/** Human duration: "240ms", "3.4s", "2m 5s". */
export function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}
